/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import "./EventGallery.css";
import { SERVER_URL } from "../../../config/serverUrl";

const serverUrl = SERVER_URL;

// Polling interval when cache is building (in ms)
const POLL_INTERVAL = 2000;
// Max poll attempts before giving up
const MAX_POLL_ATTEMPTS = 60; // 2 minutes max

const EventGallery = () => {
  const { eventName } = useParams();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventExists, setEventExists] = useState(null);
  const [galleryFolder, setGalleryFolder] = useState("bodies");
  const [loadingProgress, setLoadingProgress] = useState({
    ready: 0,
    pending: 0,
    total: 0,
    cacheBuilding: false,
    message: "טוען..."
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Refs for polling
  const pollIntervalRef = useRef(null);
  const pollAttemptsRef = useRef(0);

  // Check if event exists and get gallery settings
  useEffect(() => {
    const checkEvent = async () => {
      try {
        const response = await fetch(
          `${serverUrl}/api/check-gallery-event?event_name=${encodeURIComponent(eventName)}`
        );
        const data = await response.json();

        if (data.exists) {
          setEventExists(true);
          setGalleryFolder(data.gallery_folder || "bodies");
        } else {
          setEventExists(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error checking event:", err);
        setError("שגיאה בטעינת האירוע");
        setLoading(false);
      }
    };

    if (eventName) {
      checkEvent();
    }
  }, [eventName]);

  // Calculate loading progress from images
  const calculateProgress = (imageList, cacheBuilding) => {
    const total = imageList.length;
    const ready = imageList.filter(img => img.thumbnail_base64).length;
    const pending = total - ready;

    let message = "";
    if (cacheBuilding || pending > 0) {
      message = `טוען תמונות... (${ready}/${total})`;
    }

    return { ready, pending, total, cacheBuilding, message };
  };

  // Load gallery images
  const loadGalleryImages = useCallback(async (isPolling = false) => {
    if (!eventExists) return;

    if (!isPolling) {
      setLoading(true);
      setLoadingProgress({ ready: 0, pending: 0, total: 0, cacheBuilding: false, message: "טוען תמונות..." });
    }

    try {
      const response = await fetch(
        `${serverUrl}/api/gallery-images?event_name=${encodeURIComponent(eventName)}&folder_path=${encodeURIComponent(galleryFolder)}`
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "שגיאה בטעינת התמונות");
      }

      const data = await response.json();
      const imageList = data.images || [];
      const cacheBuilding = data.cache_building || false;

      // Update images - merge new thumbnails with existing ones
      setImages(prevImages => {
        if (prevImages.length === 0) {
          return imageList;
        }

        // Merge: keep existing thumbnails, update with new ones
        const imageMap = new Map(prevImages.map(img => [img.path, img]));
        imageList.forEach(newImg => {
          const existing = imageMap.get(newImg.path);
          if (!existing || (newImg.thumbnail_base64 && !existing.thumbnail_base64)) {
            imageMap.set(newImg.path, newImg);
          }
        });

        return Array.from(imageMap.values()).sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
      });

      const progress = calculateProgress(imageList, cacheBuilding);
      setLoadingProgress(progress);

      // Start or continue polling if there are pending images
      if (cacheBuilding || progress.pending > 0) {
        startPolling();
      } else {
        stopPolling();
      }

      if (!isPolling) {
        setInitialLoadDone(true);
      }
    } catch (err) {
      console.error("Error loading gallery:", err);
      if (!isPolling) {
        setError(err.message);
      }
    } finally {
      if (!isPolling) {
        setLoading(false);
      }
    }
  }, [eventExists, eventName, galleryFolder]);

  // Start polling for missing thumbnails
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return; // Already polling

    pollAttemptsRef.current = 0;

    pollIntervalRef.current = setInterval(() => {
      pollAttemptsRef.current += 1;

      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        stopPolling();
        return;
      }

      loadGalleryImages(true);
    }, POLL_INTERVAL);
  }, [loadGalleryImages]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Initial load
  useEffect(() => {
    if (eventExists) {
      loadGalleryImages();
    }
  }, [eventExists, galleryFolder]);

  // Handle image click for lightbox
  const handleImageClick = (image) => {
    if (image.thumbnail_base64) {
      setSelectedImage(image);
    }
  };

  // Close lightbox
  const closeLightbox = () => {
    setSelectedImage(null);
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      if (e.key === "Escape") {
        closeLightbox();
      } else if (e.key === "ArrowLeft") {
        navigateImage(-1);
      } else if (e.key === "ArrowRight") {
        navigateImage(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, images]);

  const navigateImage = (direction) => {
    if (!selectedImage || images.length === 0) return;

    // Only navigate to images that have thumbnails
    const loadedImages = images.filter(img => img.thumbnail_base64);
    const currentIndex = loadedImages.findIndex((img) => img.path === selectedImage.path);
    let newIndex = currentIndex + direction;

    if (newIndex < 0) newIndex = loadedImages.length - 1;
    if (newIndex >= loadedImages.length) newIndex = 0;

    setSelectedImage(loadedImages[newIndex]);
  };

  // Force refresh
  const handleRefresh = () => {
    stopPolling();
    pollAttemptsRef.current = 0;
    setImages([]);
    setInitialLoadDone(false);
    loadGalleryImages();
  };

  // Event not found
  if (eventExists === false) {
    return (
      <div className="event-gallery-page">
        <div className="gallery-error-container">
          <h1>האירוע לא נמצא</h1>
          <p>לא נמצא אירוע בשם זה</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="event-gallery-page">
        <div className="gallery-error-container">
          <h1>שגיאה</h1>
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-button">
            נסה שנית
          </button>
        </div>
      </div>
    );
  }

  // Calculate pending images count for display
  const pendingCount = images.filter(img => !img.thumbnail_base64).length;

  return (
    <div className="event-gallery-page">
      {/* Header */}
      <header className="gallery-header">
        <h1 className="gallery-title">גלריה</h1>
        <p className="gallery-event-name">{eventName?.split("_")[1] || eventName}</p>

        {/* Loading indicator in header when polling */}
        {initialLoadDone && pendingCount > 0 && (
          <div className="gallery-header-status">
            <div className="status-spinner"></div>
            <span>{pendingCount === 1 ? "טוען תמונה אחת נוספת..." : `טוען ${pendingCount} תמונות נוספות...`}</span>
          </div>
        )}
      </header>

      {/* Initial Loading State */}
      {loading && !initialLoadDone && (
        <div className="gallery-loading-container">
          <div className="gallery-loader">
            <div className="loader-spinner"></div>
            <p className="loader-text">{loadingProgress.message || "טוען..."}</p>
            {loadingProgress.total > 0 && (
              <div className="loader-progress">
                <div
                  className="loader-progress-bar"
                  style={{
                    width: `${(loadingProgress.ready / loadingProgress.total) * 100}%`
                  }}
                />
                <span className="loader-progress-text">
                  {loadingProgress.ready} / {loadingProgress.total}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gallery Grid - show as soon as we have some images */}
      {initialLoadDone && images.length > 0 && (
        <>
          {/* Progress bar when loading more */}
          {pendingCount > 0 && (
            <div className="gallery-loading-bar">
              <div
                className="gallery-loading-bar-fill"
                style={{
                  width: `${((images.length - pendingCount) / images.length) * 100}%`
                }}
              />
            </div>
          )}

          <div className="gallery-grid">
            {images.map((image) => (
              <div
                key={image.path}
                className={`gallery-item ${!image.thumbnail_base64 ? 'gallery-item-loading' : ''}`}
                onClick={() => handleImageClick(image)}
              >
                <span className="gallery-item-name">{image.name}</span>
                {image.thumbnail_base64 ? (
                  <img
                    src={`data:${image.thumbnail_content_type || "image/webp"};base64,${image.thumbnail_base64}`}
                    alt={image.name}
                    className="gallery-thumbnail"
                    loading="lazy"
                  />
                ) : (
                  <div className="gallery-placeholder">
                    <div className="placeholder-spinner"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && initialLoadDone && images.length === 0 && (
        <div className="gallery-empty">
          <p>אין תמונות בגלריה</p>
          <button onClick={handleRefresh} className="retry-button">
            רענן
          </button>
        </div>
      )}

      {/* Refresh button - always visible after initial load */}
      {initialLoadDone && images.length > 0 && (
        <button
          className="gallery-refresh-button"
          onClick={handleRefresh}
          title="רענן גלריה"
        >
          <svg className="refresh-icon" viewBox="0 0 24 24">
            <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          רענן
        </button>
      )}

      {/* Lightbox */}
      {selectedImage && (
        <div className="gallery-lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            &times;
          </button>
          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage(1);
            }}
          >
            &#8250;
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={`data:${selectedImage.thumbnail_content_type || "image/webp"};base64,${selectedImage.thumbnail_base64}`}
              alt={selectedImage.name}
              className="lightbox-image"
            />
            <p className="lightbox-caption">{selectedImage.name}</p>
          </div>
          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              navigateImage(-1);
            }}
          >
            &#8249;
          </button>
        </div>
      )}
    </div>
  );
};

export default EventGallery;
